import React, { useEffect, useState } from "react";
import { Box, Grid } from "theme-ui";

import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import { themevals } from "theme/themevals";
import { mntStatCard } from "theme/maintenanceTokens";
import { getPlanFinancials, PlanFinancials } from "api/Dashboard";

/**
 * PLAN_FINANCIAL_CARDS.md - the money row on Plan Detail, beneath the
 * operational stat cards.
 *
 * OPERATOR ONLY. This component is rendered from `PlanDetail` and never
 * from `PlanClientDetail`: it shows COST and MARGIN, and the client
 * sees what they are billed, never what we pay providers. The endpoint
 * behind it is isCompany-gated for the same reason, so a mistake here
 * is a blank card rather than a leak.
 *
 * WHY THERE IS NO "ESTIMATED VALUE" BLENDING NTE. The original spec
 * asked whether that card should be quoted-to-date or quoted + NTE for
 * the unquoted remainder. DASHBOARD_REDESIGN_SPEC section 8.2 settled
 * the same question for the dashboard and dropped the unpriced measure:
 * an unquoted work order is identified rather than sold, and an NTE is
 * a ceiling rather than a prediction. Blending it here would make this
 * header and the dashboard report different money for the same plan.
 *
 * So the unquoted remainder is a COUNT beside Approved. "41 sites not
 * yet quoted" is a fact; a dollar figure built from ceilings is not.
 */

const money = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

export default function PlanFinancialCards({ planId }: { planId: string }) {
  const [data, setData] = useState<PlanFinancials | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const result = await getPlanFinancials(planId);
        if (!cancelled) setData(result);
      } catch {
        // The row is supplementary to the page. A failure here hides
        // the money and leaves the plan working, rather than taking
        // Plan Detail down with it.
        if (!cancelled) setFailed(true);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [planId]);

  if (failed) return null;

  const cards: Array<{ label: string; value: React.ReactNode; sub?: string }> = [
    {
      label: "Approved",
      value: data ? money(data.approved.value) : "-",
      sub: data
        ? data.unquotedWorkOrders > 0
          ? data.approved.count +
            " accepted, " +
            data.unquotedWorkOrders +
            " not yet quoted"
          : data.approved.count + " accepted quotes"
        : undefined,
    },
    {
      label: "Invoiced",
      value: data ? money(data.invoiced.value) : "-",
      sub: data
        ? data.approved.value > 0
          ? Math.round((data.invoiced.value / data.approved.value) * 100) +
            "% of approved"
          : data.invoiced.count + " invoices"
        : undefined,
    },
    {
      label: "Collected",
      value: data ? money(data.collected) : "-",
      sub: data
        ? data.invoiced.value > 0
          ? Math.round((data.collected / data.invoiced.value) * 100) +
            "% of invoiced"
          : "Nothing invoiced yet"
        : undefined,
    },
    {
      label: "Margin",
      value: data
        ? data.margin === null
          ? "n/a"
          : data.margin.toFixed(1) + "%"
        : "-",
      // Cost is stated rather than left implicit: margin without its
      // cost is a percentage nobody can check.
      //
      // "on billed work" is doing real work in that sentence. Until
      // Session 81 this cost was every PO on the plan, including jobs
      // the client had not been invoiced for, divided into billed
      // revenue — LCG read -81.9% against a true 25.2%. The unbilled
      // half now has its own card beside this one, and the wording says
      // which half this is so the two are never re-merged by someone
      // tidying up.
      sub: data
        ? data.margin === null
          ? "No work invoiced yet"
          : "After " + money(data.cost) + " provider cost on billed work"
        : undefined,
    },
    {
      label: "Committed, not yet billed",
      value: data ? money(data.committedUnbilled) : "-",
      // NOT a loss and NOT in the margin. It is money promised to
      // providers for work the client has not been invoiced for — on a
      // plan that runs for years there is always some, and it only
      // becomes a problem when it stops turning into invoices.
      sub: data
        ? data.committedUnbilled > 0
          ? "Provider cost on work not yet invoiced"
          : "Everything ordered has been billed"
        : undefined,
    },
  ];

  return (
    <Box sx={{ marginTop: "12px" }}>
      <Grid columns={[1, 2, 5]} gap="12px" sx={{ alignItems: "stretch" }}>
        {cards.map((card) => (
          <Box key={card.label} sx={mntStatCard()}>
            <Subtext sx={{ color: themevals.colors.dkgrey }}>
              {card.label}
            </Subtext>
            <H5 sx={{ margin: "2px 0 0" }}>{card.value}</H5>
            {card.sub && (
              <Subtext sx={{ color: themevals.colors.dkgrey }}>
                {card.sub}
              </Subtext>
            )}
          </Box>
        ))}
      </Grid>
    </Box>
  );
}
